import type { ChangeEvent, FocusEvent, ReactNode } from 'react';
import { useCallback, useState } from 'react';

import AlertCircle from '@m/assets/svg/alert-circle';
import ArrowRight from '@m/assets/svg/arrow-right';
import KeycloakLoginButton from '@m/components/login/KeycloakLoginButton/KeycloakLoginButton';
import MicrosoftSignInButton from '@m/components/login/MicrosoftSignInButton';
import { Box, Text } from '@m/components/uikit';
import theme from '@m/theme/theme';
import cleanNumber from '@m/utils/cleanNumber';
import { formatPhoneInput } from '@m/utils/formatPhoneInput';
import { heapTrackProperties } from '@m/utils/heap';
import isPhone from '@m/utils/validators/isPhone';
import { useMedia } from 'react-use';

import { Heading, Step } from '@m/legacy-auth/components/PageOverlay';
import Input from '@m/legacy-auth/components/TextInput';

import {
  ButtonBox,
  EnterPhoneBox,
  EnterPhoneButton,
  PhoneLoginButton,
  PhoneLoginFields,
  TopRow,
} from './Login.styled';

type LoginProps = {
  active?: boolean;
  disableSSOAuth?: boolean;
  errorMessage?: ReactNode;
  hasError?: boolean;
  isLoading?: boolean;
  loginAttempted?: boolean;
  loginPhone: (phone: string) => void;
  name: string;
  updateContentSize?: (size: { height: string | number; width: string | number }) => void;
};

export default function Login({
  name,
  active = false,
  updateContentSize = () => {},
  loginPhone,
  loginAttempted,
  isLoading,
  hasError,
  errorMessage,
  disableSSOAuth,
}: LoginProps) {
  const [loginMethod, setLoginMethod] = useState<'phone'>();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneInputValue, setPhoneInputValue] = useState('');
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  const isMobile = useMedia('(max-width: 439px)');
  const handlePhoneInputFocus = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => {
      setLoginMethod('phone');
      if (value.trim() === '') {
        setPhoneInputValue('+1 ');
      }
    },
    [],
  );

  const handlePhoneInputBlur = useCallback(
    ({ target: { value } }: FocusEvent<HTMLInputElement>) => {
      if (value.trim() === '+1') {
        setPhoneInputValue('');
      }
    },
    [],
  );

  const handlePhoneInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      if (value.trim() === '') {
        setPhoneInputValue('+1 ');
        if (phoneIsValid) {
          setPhoneIsValid(false);
        }
      } else {
        const phone = cleanNumber(value);
        setPhoneNumber(phone);

        const phoneFormatted = formatPhoneInput(phone);
        setPhoneInputValue(phoneFormatted);

        const phoneTest = phone.replace(/^1/, '');
        if (!phoneIsValid && isPhone(phoneTest)) {
          setPhoneIsValid(true);
        } else if (value && !isPhone(phoneTest)) {
          setPhoneIsValid(false);
        }
      }
    },
    [phoneIsValid],
  );

  const handlePhoneLogin = useCallback(() => {
    heapTrackProperties('Login', {
      login_method: 'phone',
    });
    loginPhone(phoneNumber);
  }, [loginPhone, phoneNumber]);

  const mobileHeight = disableSSOAuth ? 208 : 385;
  const desktopHeight = disableSSOAuth ? 220 : 282;

  return (
    <Step
      active={active}
      width={isMobile ? 320 : 460}
      height={isMobile ? mobileHeight : desktopHeight}
      name="login"
      updateContentSize={updateContentSize}
    >
      <Heading>Login to Metropolis</Heading>
      <Box
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        paddingBottom="12px"
      >
        <Box flexDirection="row" justifyContent="center" paddingTop="24px" maxWidth="240px">
          <Text fontSize="16px" lineHeight={1.35} textAlign="center">
            <span>Login with your phone number</span>
            {!disableSSOAuth && <div> or email</div>}
          </Text>
        </Box>
        <ButtonBox disableSSOAuth={false}>
          <TopRow disableSSOAuth={false}>
            <EnterPhoneBox>
              <EnterPhoneButton
                type="button"
                isHidden={loginMethod === 'phone'}
                onClick={() => setLoginMethod('phone')}
              >
                <span>Sign in with Phone</span>
              </EnterPhoneButton>
              <PhoneLoginFields isActive={loginMethod === 'phone'}>
                {loginMethod === 'phone' && (
                  <Input
                    required
                    type="code"
                    step="any"
                    label="Phone Number"
                    name="phone"
                    pattern="[0-9]*"
                    autoFocus
                    onChange={handlePhoneInputChange}
                    onFocus={handlePhoneInputFocus}
                    onBlur={handlePhoneInputBlur}
                    value={phoneInputValue}
                    isEmpty={phoneInputValue.length === 0}
                  />
                )}
                <PhoneLoginButton onClick={handlePhoneLogin} isEnabled={phoneIsValid} type="button">
                  <ArrowRight width="20px" height="20px" fill={theme.colors.blue2} />
                </PhoneLoginButton>
              </PhoneLoginFields>
            </EnterPhoneBox>
            <div css="margin-top: 12px;">
              <KeycloakLoginButton />
            </div>
          </TopRow>
          <TopRow>
            {!disableSSOAuth && (
              <div css="margin-top: 12px;">
                <MicrosoftSignInButton />
              </div>
            )}
          </TopRow>
        </ButtonBox>
        {loginAttempted && isLoading === false && hasError === true && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            width="100%"
            alignItems="center"
            justifyContent="center"
            height="36px"
            paddingX="20px"
          >
            <AlertCircle width={18} height={18} css="margin-right: 6px" fill={theme.colors.coral} />
            <Text fontSize="15px">{errorMessage ?? 'No matching account found.'}</Text>
          </Box>
        )}
      </Box>
    </Step>
  );
}
